import * as Sentry from '@sentry/browser'
import { watch } from 'vue'

/**
 * Navigation anomaly observability layer.
 *
 * Records every navigation (virtual router in NavigationStore, real routes in
 * vue-router, popstate via useBrowserHistoryBridge) as Sentry breadcrumbs and
 * keeps a short in-memory history. Detectors run on each record and, when a
 * pattern crosses its threshold, emit ONE structured Sentry message event
 * (never captureException — these are observability signals, not exceptions)
 * with enough context to reconstruct the sequence: recent route history,
 * pending/failed requests, virtual-router state and loading state.
 *
 * Detected patterns:
 *  - navigation-loop        A↔B ping-pong / repeated route cycles (high)
 *  - rapid-navigation       many route changes with no user interaction (medium)
 *  - programmatic-after-popstate  app navigates by itself right after Back/Forward (medium)
 *  - redirect-loop          a router guard keeps redirecting the same way (high)
 *  - loading-timeout        a view stays in loading state past the threshold;
 *                           high when the view was restored via Back/Forward (the
 *                           "infinite loading after returning to a module" bug)
 *
 * Noise control: every anomaly type+route fingerprint has a cooldown and there
 * is a hard cap of events per page load. Normal user Back/Forward usage never
 * produces an event by itself — detectors require repetition, short intervals
 * and (where it applies) absence of recent user interaction.
 */

const envInt = (key, fallback) => {
  const raw = Number(import.meta.env[key])
  return Number.isFinite(raw) && raw > 0 ? raw : fallback
}

const CONFIG = {
  // Ring buffer of recent navigations kept for detection/context.
  historyLimit: 30,
  // A navigation within this window after pointer/key input counts as user-driven.
  userIntentWindowMs: 1500,
  // A push/replace within this window after a popstate is "programmatic after Back/Forward".
  postPopstateWindowMs: 800,
  postPopstateMinCount: 2,
  // Rapid navigation: N auto-likely route changes inside the window.
  rapidWindowMs: 3000,
  rapidMinCount: 8,
  // Ping-pong / cycle detection window.
  loopWindowMs: 8000,
  // Redirect loop: same guard redirect N times inside the window.
  redirectLoopWindowMs: 10000,
  redirectLoopMinCount: 3,
  // Loading watchdog. Cold starts of minInstances:0 gateways can take several
  // seconds, so the default stays well above a normal slow load.
  loadingTimeoutMs: envInt('VITE_NAV_LOADING_TIMEOUT_MS', 20000),
  // How recent a back/forward must be for a loading timeout to count as
  // "restored via history navigation".
  historyRestoreWindowMs: 10000,
  // One Sentry event per anomaly fingerprint per cooldown, hard cap per page load.
  anomalyCooldownMs: envInt('VITE_NAV_ANOMALY_COOLDOWN_MS', 60000),
  maxEventsPerPageLoad: 25
}

// Query params that are safe to keep when recording URLs. Everything else is
// dropped (e.g. oobCode carries a password-reset credential).
const SAFE_QUERY_PARAMS = ['view', 'lessonId', 'moduleId', 'scrollTo', 'status', 'returnRoute']

const PRIORITY_LEVEL = { high: 'error', medium: 'warning', low: 'info' }

function sanitizeUrl(rawUrl) {
  try {
    const url = new URL(rawUrl, window.location.origin)
    const safeQuery = new URLSearchParams()
    for (const key of SAFE_QUERY_PARAMS) {
      if (!url.searchParams.has(key)) continue
      const value = url.searchParams.get(key)
      // returnRoute may itself contain sensitive nested query params (e.g. oobCode)
      // so only keep its path portion.
      if (key === 'returnRoute') safeQuery.set(key, String(value || '').split('?')[0])
      else safeQuery.set(key, value)
    }
    const query = safeQuery.toString()
    return url.pathname + (query ? `?${query}` : '')
  } catch {
    return String(rawUrl).split('?')[0]
  }
}

class NavigationMonitorService {
  // Exposed so callers that record raw URLs (router afterEach) can strip
  // non-whitelisted query params before they reach breadcrumbs.
  sanitizeUrl(rawUrl) {
    return sanitizeUrl(rawUrl)
  }

  constructor() {
    this.pageLoadId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`
    this.navigationCounter = 0
    this.history = []
    this.lastPopstate = null // { direction, ts }
    this.lastUserInteractionAt = 0
    this.consecutivePostPopstatePushes = 0
    this.guardRedirects = [] // { key, ts }
    this.pendingRequests = new Map() // id → { url, method, startedAt }
    this.requestSeq = 0
    this.lastFailedRequests = [] // last 5 failed requests
    this.loadingWatchers = new Map() // module → { startedAt, timer, timedOut }
    this.reportedAt = new Map() // anomaly fingerprint → ts
    this.eventsSent = 0
    this.stateProvider = null
    this.interactionListenersInstalled = false
    this.installInteractionListeners()
  }

  // Passive global listeners used only to tell user-driven navigations apart
  // from programmatic ones (a navigation long after the last input is
  // unlikely to be a direct user action).
  installInteractionListeners() {
    if (this.interactionListenersInstalled || typeof window === 'undefined') return
    this.interactionListenersInstalled = true
    const markInteraction = () => {
      this.lastUserInteractionAt = Date.now()
    }
    window.addEventListener('pointerdown', markInteraction, { capture: true, passive: true })
    window.addEventListener('keydown', markInteraction, { capture: true, passive: true })
  }

  // NavigationStore registers a snapshot function so every event can include
  // the virtual-router state without this service importing Pinia stores.
  registerStateProvider(provider) {
    this.stateProvider = provider
  }

  virtualStateSnapshot() {
    try {
      return this.stateProvider ? this.stateProvider() : null
    } catch {
      return null
    }
  }

  /** Called by useBrowserHistoryBridge when the browser Back/Forward fires. */
  markPopstate(direction) {
    this.lastPopstate = { direction, ts: Date.now() }
    Sentry.addBreadcrumb({
      category: 'navigation.popstate',
      message: `browser ${direction}`,
      level: 'info'
    })
  }

  /**
   * Records one navigation and runs the anomaly detectors.
   *
   * @param {object} entry
   * @param {string} entry.from   route/page the app navigated from
   * @param {string} entry.to     route/page the app navigated to
   * @param {string} entry.action 'push' | 'replace' | 'back' | 'forward' | 'redirect'
   * @param {string} entry.source 'virtual' (NavigationStore) | 'router' (vue-router)
   */
  recordNavigation({ from, to, action, source }) {
    const ts = Date.now()
    const sincePopstateMs = this.lastPopstate ? ts - this.lastPopstate.ts : null
    const sinceInteractionMs = this.lastUserInteractionAt ? ts - this.lastUserInteractionAt : null
    const record = {
      from,
      to,
      action,
      source,
      ts,
      sincePopstateMs,
      sinceInteractionMs,
      // No recent input and not the Back/Forward bridge itself → likely the
      // app navigating on its own.
      autoLikely:
        (sinceInteractionMs == null || sinceInteractionMs > CONFIG.userIntentWindowMs) &&
        !(action === 'back' || action === 'forward'),
      navigationId: ++this.navigationCounter
    }
    this.history.push(record)
    if (this.history.length > CONFIG.historyLimit) this.history.shift()

    Sentry.addBreadcrumb({
      category: `navigation.${source}`,
      message: `${action}: ${from} → ${to}`,
      level: 'info',
      data: {
        navigationId: record.navigationId,
        sincePopstateMs,
        autoLikely: record.autoLikely
      }
    })

    this.detectProgrammaticAfterPopstate(record)
    this.detectNavigationLoop(record)
    this.detectRapidNavigation(record)
  }

  /** Called from router guards when they force a redirect. */
  recordGuardRedirect({ from, to, reason }) {
    const ts = Date.now()
    const key = `${sanitizeUrl(from)}→${to}:${reason}`
    this.guardRedirects.push({ key, ts })
    this.guardRedirects = this.guardRedirects.filter(
      (r) => ts - r.ts <= CONFIG.redirectLoopWindowMs
    )
    this.recordNavigation({ from: sanitizeUrl(from), to, action: 'redirect', source: 'router' })
    Sentry.addBreadcrumb({
      category: 'navigation.redirect',
      message: `guard redirect (${reason}): ${sanitizeUrl(from)} → ${to}`,
      level: 'warning'
    })

    const repeats = this.guardRedirects.filter((r) => r.key === key).length
    if (repeats >= CONFIG.redirectLoopMinCount) {
      this.reportAnomaly({
        type: 'redirect-loop',
        priority: 'high',
        fingerprintKey: key,
        message: `Guard redirect repeated ${repeats}x in ${CONFIG.redirectLoopWindowMs / 1000}s: ${key}`,
        details: { redirect: key, repeats, reason }
      })
    }
  }

  // ---- Detectors ----------------------------------------------------------

  detectProgrammaticAfterPopstate(record) {
    const isPostPopstatePush =
      (record.action === 'push' || record.action === 'replace' || record.action === 'redirect') &&
      record.sincePopstateMs != null &&
      record.sincePopstateMs <= CONFIG.postPopstateWindowMs
    this.consecutivePostPopstatePushes = isPostPopstatePush
      ? this.consecutivePostPopstatePushes + 1
      : 0

    if (this.consecutivePostPopstatePushes >= CONFIG.postPopstateMinCount) {
      this.reportAnomaly({
        type: 'programmatic-after-popstate',
        priority: 'medium',
        fingerprintKey: `${record.from}→${record.to}`,
        message: `Programmatic navigation fired ${this.consecutivePostPopstatePushes}x right after browser Back/Forward (${record.from} → ${record.to})`,
        details: {
          consecutiveCount: this.consecutivePostPopstatePushes,
          sincePopstateMs: record.sincePopstateMs,
          lastPopstateDirection: this.lastPopstate?.direction ?? null
        }
      })
    }
  }

  detectNavigationLoop(record) {
    const windowStart = record.ts - CONFIG.loopWindowMs
    const recent = this.history.filter((r) => r.ts >= windowStart)
    if (recent.length < 5) return

    // Cycles of period 2 (A↔B ping-pong) or 3 (A→B→C→A→B→C) over the last
    // entries, requiring at least two full repetitions of the sequence.
    for (const period of [2, 3]) {
      const needed = period * 2 + 1
      if (recent.length < needed) continue
      const tail = recent.slice(-needed)
      const isCycle = tail.every((r, i) => i < period || r.to === tail[i - period].to)
      if (!isCycle) continue
      // Normal fast clicking is not a loop: require that most hops in the
      // cycle happened without recent user input.
      const autoHops = tail.filter((r) => r.autoLikely).length
      if (autoHops < Math.ceil(tail.length / 2)) continue

      const routes = [...new Set(tail.slice(0, period).map((r) => r.to))]
      this.reportAnomaly({
        type: 'navigation-loop',
        priority: 'high',
        fingerprintKey: routes.slice().sort().join('↔'),
        message: `Navigation cycle detected between [${routes.join(' ↔ ')}] (${tail.length} hops in ${CONFIG.loopWindowMs / 1000}s, ${autoHops} without user input)`,
        details: {
          cycleRoutes: routes,
          cyclePeriod: period,
          hops: tail.length,
          autoLikelyHops: autoHops
        }
      })
      return
    }
  }

  detectRapidNavigation(record) {
    const windowStart = record.ts - CONFIG.rapidWindowMs
    const recent = this.history.filter((r) => r.ts >= windowStart)
    if (recent.length < CONFIG.rapidMinCount) return
    // Diagnostic signal only — several legitimate updates can happen during a
    // transition, so this fires only when the burst looks machine-driven.
    const autoCount = recent.filter((r) => r.autoLikely).length
    if (autoCount < Math.ceil(recent.length / 2)) return

    this.reportAnomaly({
      type: 'rapid-navigation',
      priority: 'medium',
      fingerprintKey: record.to,
      message: `${recent.length} route changes in ${CONFIG.rapidWindowMs / 1000}s (${autoCount} without user input), last: ${record.from} → ${record.to}`,
      details: { navigationsInWindow: recent.length, autoLikelyCount: autoCount }
    })
  }

  // ---- Loading watchdog ---------------------------------------------------

  /**
   * Watches a store's loading ref and reports when a view stays loading past
   * the timeout. Meant to be called once from a Pinia setup store:
   *   navigationMonitor.watchLoading('transformLearning', isLoading)
   */
  watchLoading(moduleName, loadingRef, { timeoutMs = CONFIG.loadingTimeoutMs } = {}) {
    watch(
      loadingRef,
      (isLoading) => {
        if (isLoading) this.onLoadingStart(moduleName, timeoutMs)
        else this.onLoadingEnd(moduleName)
      },
      { immediate: true }
    )
  }

  onLoadingStart(moduleName, timeoutMs) {
    const existing = this.loadingWatchers.get(moduleName)
    if (existing?.timer) clearTimeout(existing.timer)

    const startedAt = Date.now()
    Sentry.addBreadcrumb({
      category: 'loading',
      message: `loading:start ${moduleName}`,
      level: 'info',
      data: { module: moduleName }
    })
    const timer = setTimeout(() => this.onLoadingTimeout(moduleName, timeoutMs), timeoutMs)
    this.loadingWatchers.set(moduleName, { startedAt, timer, timedOut: false })
  }

  onLoadingEnd(moduleName) {
    const entry = this.loadingWatchers.get(moduleName)
    if (!entry) return
    clearTimeout(entry.timer)
    this.loadingWatchers.delete(moduleName)
    const durationMs = Date.now() - entry.startedAt
    Sentry.addBreadcrumb({
      category: 'loading',
      message: `loading:end ${moduleName}`,
      level: entry.timedOut ? 'warning' : 'info',
      data: { module: moduleName, durationMs, afterTimeout: entry.timedOut }
    })
  }

  onLoadingTimeout(moduleName, timeoutMs) {
    const entry = this.loadingWatchers.get(moduleName)
    if (!entry) return
    entry.timedOut = true

    const lastHistoryNav = [...this.history]
      .reverse()
      .find((r) => r.action === 'back' || r.action === 'forward')
    const restoredViaHistory =
      !!lastHistoryNav && entry.startedAt - lastHistoryNav.ts <= CONFIG.historyRestoreWindowMs

    Sentry.addBreadcrumb({
      category: 'loading',
      message: `loading:timeout ${moduleName}`,
      level: 'error',
      data: { module: moduleName, timeoutMs, restoredViaHistory }
    })

    this.reportAnomaly({
      type: 'loading-timeout',
      // A view stuck loading after Back/Forward is the known blocking bug;
      // a slow load during normal navigation is diagnostic.
      priority: restoredViaHistory ? 'high' : 'medium',
      fingerprintKey: moduleName,
      message: `View '${moduleName}' still loading after ${timeoutMs / 1000}s${restoredViaHistory ? ' (restored via browser Back/Forward)' : ''}`,
      details: {
        module: moduleName,
        timeoutMs,
        restoredViaHistory,
        returnedFrom: restoredViaHistory ? lastHistoryNav.from : null,
        returnedTo: restoredViaHistory ? lastHistoryNav.to : null,
        lastHistoryAction: lastHistoryNav
          ? { action: lastHistoryNav.action, agoMs: Date.now() - lastHistoryNav.ts }
          : null
      }
    })
  }

  // ---- HTTP request tracking (wired from RequestService interceptors) -----

  trackRequestStart(config) {
    if (!config) return
    const id = ++this.requestSeq
    config.__navMonitorRequestId = id
    this.pendingRequests.set(id, {
      url: sanitizeUrl(config.url || ''),
      method: config.method || 'get',
      startedAt: Date.now()
    })
  }

  trackRequestEnd(config, { failed = false, status = null, code = null } = {}) {
    const id = config?.__navMonitorRequestId
    if (!id) return
    const entry = this.pendingRequests.get(id)
    this.pendingRequests.delete(id)
    if (failed && entry) {
      this.lastFailedRequests.push({
        ...entry,
        status,
        code,
        failedAt: Date.now()
      })
      if (this.lastFailedRequests.length > 5) this.lastFailedRequests.shift()
    }
  }

  pendingRequestsSnapshot() {
    const now = Date.now()
    return [...this.pendingRequests.values()].map((r) => ({
      ...r,
      pendingForMs: now - r.startedAt
    }))
  }

  // ---- Reporting ----------------------------------------------------------

  reportAnomaly({ type, priority, fingerprintKey, message, details }) {
    const fingerprint = `${type}:${fingerprintKey}`
    const now = Date.now()
    const lastReported = this.reportedAt.get(fingerprint)
    if (lastReported && now - lastReported < CONFIG.anomalyCooldownMs) return
    if (this.eventsSent >= CONFIG.maxEventsPerPageLoad) return
    this.reportedAt.set(fingerprint, now)
    this.eventsSent++

    const virtualState = this.virtualStateSnapshot()
    Sentry.withScope((scope) => {
      scope.setTag('anomaly.type', type)
      scope.setTag('anomaly.priority', priority)
      scope.setTag('nav.pageLoadId', this.pageLoadId)
      scope.setLevel(PRIORITY_LEVEL[priority] || 'warning')
      // Group by anomaly type + routes involved, not by message text.
      scope.setFingerprint(['navigation-anomaly', type, fingerprintKey])
      scope.setContext('navigation_anomaly', {
        type,
        priority,
        ...details,
        navigationId: this.navigationCounter,
        recentNavigations: this.history.slice(-15).map((r) => ({
          seq: r.navigationId,
          route: `${r.from} → ${r.to}`,
          action: r.action,
          source: r.source,
          autoLikely: r.autoLikely,
          sincePopstateMs: r.sincePopstateMs,
          ts: new Date(r.ts).toISOString()
        })),
        lastPopstate: this.lastPopstate
          ? { direction: this.lastPopstate.direction, agoMs: now - this.lastPopstate.ts }
          : null,
        msSinceLastUserInteraction: this.lastUserInteractionAt
          ? now - this.lastUserInteractionAt
          : null
      })
      scope.setContext('app_state', {
        url: sanitizeUrl(window.location.href),
        virtualRouter: virtualState,
        loadingModules: [...this.loadingWatchers.keys()],
        pendingRequests: this.pendingRequestsSnapshot(),
        lastFailedRequests: this.lastFailedRequests,
        viewport: { width: window.innerWidth, height: window.innerHeight }
      })
      Sentry.captureMessage(`[navigation-anomaly] ${message}`, PRIORITY_LEVEL[priority] || 'warning')
    })
  }
}

const navigationMonitor = new NavigationMonitorService()

export default navigationMonitor
